<template>
    <div class="event-signups">
        <div class="text-center mb-3 export-controls">
            <select v-model="exportFormat" class="form-control export-format-select" :aria-label="$trans('event.manage.export_format')">
                <option value="xlsx">Excel (.xlsx)</option>
                <option value="xls">Excel 97-2003 (.xls)</option>
                <option value="xlsm">Excel Macro (.xlsm)</option>
                <option value="csv">CSV (.csv)</option>
            </select>
            <button class="btn btn-primary" @click="exportTable">
                <i class="fas fa-download"></i> {{ $trans('event.manage.export_table') }}
            </button>
        </div>
        <div class="table-container">
            <div v-if="sortedSignups.length === 0" class="text-center p-4">
                {{ $trans('event.manage.no_signups') }}
            </div>
            <div v-else>
                <table class="table">
                    <thead>
                        <tr>
                            <th>{{ $trans('event.manage.row_number') }}</th>
                            <th>{{ $trans('event.fields.signup_date') }}</th>
                            <th>{{ $trans('event.fields.field_first_name') }}</th>
                            <th>{{ $trans('event.fields.field_last_name') }}</th>
                            <th>{{ $trans('event.fields.field_email') }}</th>
                            <th v-for="column in dynamicColumns" :key="column">{{ column }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(signup, index) in sortedSignups" :key="signup.id">
                            <td>{{ index + 1 }}</td>
                            <td>{{ formatDate(signup.created_at) }}</td>
                            <td>{{ signup.first_name }}</td>
                            <td>{{ signup.last_name }}</td>
                            <td><a :href="`mailto:${signup.email}`">{{ signup.email }}</a></td>
                            <td v-for="column in dynamicColumns" :key="column">
                                <template v-if="column === 'טלפון'">
                                    <a :href="`tel:${signup.properties[column]}`">{{ signup.properties[column] }}</a>
                                </template>
                                <template v-else-if="column === 'אם תרצה להצטרף לניוזלטר שלנו?'">
                                    <span class="newsletter-cell">
                                        <i :class="[
                                            'fas',
                                            isSubscribed(signup.properties[column]) ? 'fa-check-square text-success' : 'fa-square'
                                        ]"></i>
                                    </span>
                                </template>
                                <template v-else>
                                    {{ signup.properties[column] || '-' }}
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import * as XLSX from 'xlsx';

export default {
    name: 'EventSignups',
    props: {
        eventId: {
            type: Number,
            required: true
        },
        signups: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            event: window.app?.event || {},
            types: {
                TYPE_TEL: 9,
                TYPE_TEXTAREA: 11,
                TYPE_DROPDOWN: 1
            },
            dynamicColumns: [],
            exportFormat: 'xlsx'
        };
    },
    mounted() {
        const columns = new Set();
        this.signups.forEach(signup => {
            const propertiesMap = {};
            if (signup.all_properties) {
                signup.all_properties.split(',').forEach(propertyString => {
                    const [name, value] = propertyString.split(':');
                    columns.add(name);
                    propertiesMap[name] = value;
                });
            }
            signup.properties = propertiesMap;
        });

        this.dynamicColumns = Array.from(columns);
        this.$forceUpdate();
    },
    computed: {
        sortedSignups() {
    // מיון לפי תאריך (הכי חדש קודם)
    const sorted = this.signups.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    // סינון רשמות כפולות - השארת הרשמה הכי חדשה מכל מייל
    const uniqueEmails = new Map();
    
    sorted.forEach(signup => {
        if (!uniqueEmails.has(signup.email)) {
            uniqueEmails.set(signup.email, signup);
        }
    });
    
    // החזרת המערך הייחודי
    return Array.from(uniqueEmails.values());
}
    },
    methods: {
        formatDate(date) {
            return moment(date).format('DD/MM/YYYY HH:mm');
        },
        formatPhoneNumber(number) {
            // Implement phone number formatting logic here

            return number;
        },
        isSubscribed(value) {
            return value === 'כן';
        },
        buildExportData() {
            // Build the data straight from the already de-duplicated `sortedSignups`,
            // so the row numbering is sequential over the unique rows and never
            // collides with the email-deduplication logic in `sortedSignups`.
            const newsletterColumn = 'אם תרצה להצטרף לניוזלטר שלנו?';
            const headers = [
                this.$trans('event.manage.row_number'),
                this.$trans('event.fields.signup_date'),
                this.$trans('event.fields.field_first_name'),
                this.$trans('event.fields.field_last_name'),
                this.$trans('event.fields.field_email'),
                ...this.dynamicColumns
            ];

            const rows = this.sortedSignups.map((signup, index) => {
                const row = [
                    index + 1,
                    this.formatDate(signup.created_at),
                    signup.first_name,
                    signup.last_name,
                    signup.email
                ];
                this.dynamicColumns.forEach(column => {
                    const value = signup.properties ? signup.properties[column] : undefined;
                    if (column === newsletterColumn) {
                        row.push(this.isSubscribed(value) ? 'כן' : 'לא');
                    } else {
                        row.push(value != null ? value : '');
                    }
                });
                return row;
            });

            return [headers, ...rows];
        },
        downloadBlob(blob, filename) {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        },
        exportTable() {
            const data = this.buildExportData();
            const format = this.exportFormat;
            const worksheet = XLSX.utils.aoa_to_sheet(data);

            if (format === 'csv') {
                // Prepend a UTF-8 BOM so Excel renders the Hebrew columns correctly.
                const csv = '﻿' + XLSX.utils.sheet_to_csv(worksheet);
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                this.downloadBlob(blob, 'signups.csv');
                return;
            }

            const bookTypes = { xlsx: 'xlsx', xls: 'biff8', xlsm: 'xlsm' };
            const bookType = bookTypes[format] || 'xlsx';
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Signups');
            XLSX.writeFile(workbook, `signups.${format}`, { bookType });
        }
    }
};
</script>
<style scoped>
.export-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
}

.export-format-select {
    width: auto;
    min-width: 170px;
}

.table-container {
    max-height: calc(100vh - 200px); /* Adjust 200px based on your header height */
    overflow-y: auto;
    border-radius: 8px;
    background: white;
}

table {
    width: 100%;
    border-collapse: collapse;
}

thead {
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;
}

th {
    padding: 12px;
    text-align: right;
    border-bottom: 2px solid #eee;
}

td {
    padding: 12px;
    border-bottom: 1px solid #eee;
}

.newsletter-cell {
    text-align: center;
}

.newsletter-cell i {
    font-size: 1.2em;
}

.fa-check {
    color: #4CAF50;
}

.fa-times {
    color: #F44336;
}
</style>
